import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import MessageBubble from "./MessageBubble";
import TypingIndicator from "./TypingIndicator";
import {
    askQuestion,
    createConversation,
    listConversations,
    renameConversation,
    deleteConversation as apiDeleteConversation,
    getConversationMessages,
} from "../../axios-client";
import "../../assets/styles/ChatPanel.css";
import { useSoftUIController } from "context";
import PropTypes from "prop-types";


/* ─── component ─────────────────────────────────────────────── */
const ChatPanel = ({ onClose }) => {
    const [controller] = useSoftUIController();
    const userUuidRef = useRef(controller.profileData?.uuid || null);

    const [conversations, setConversations] = useState([]);
    const [convsLoading, setConvsLoading]   = useState(true);
    const [activeConvId, setActiveConvId]   = useState(null);

    const [renamingId, setRenamingId]   = useState(null);
    const [renameValue, setRenameValue] = useState("");

    const [messages, setMessages] = useState([]);
    const [input, setInput]       = useState("");
    const [isTyping, setIsTyping] = useState(false);

    const [minimized, setMinimized]     = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const messagesEndRef = useRef(null);
    const renameInputRef = useRef(null);

    /* ── 1. load conversation list on mount ── */
    useEffect(() => {
        const load = async () => {
            setConvsLoading(true);
            try {
                const res = await listConversations(userUuidRef.current);
                const convs = (res.data || []).map((c) => ({
                    ...c,
                    title: c.title || "New conversation",
                }));
                setConversations(convs);
            } catch (err) {
                console.error("Failed to load conversations:", err);
            } finally {
                setConvsLoading(false);
            }
        };
        load();
    }, []);

    /* ── 2. auto-scroll ── */
    useEffect(() => {
        if (!minimized) messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isTyping, minimized]);

    /* ── 3. focus rename input when it appears ── */
    useEffect(() => {
        if (renamingId) renameInputRef.current?.focus();
    }, [renamingId]);

    /* ── load messages for a conversation ── */
    const loadMessages = useCallback(async (convId) => {
        try {
            const res = await getConversationMessages(convId, 1, 50);
            const msgs = (res.data.messages || []).map((msg) => ({
                id: msg.id,
                type: msg.sender,
                text: msg.content,
                timestamp: new Date(msg.created_at).getTime(),
                sources: Array.isArray(msg.sources) ? msg.sources : [],
            }));
            setMessages(msgs);
        } catch (err) {
            console.error("Failed to load messages:", err);
            setMessages([]);
        }
    }, []);

    /* ── conversation CRUD ── */
    const handleCreateConversation = useCallback(async () => {
        try {
            const res = await createConversation(userUuidRef.current);
            const DEFAULT_TITLE = "New conversation";
            await renameConversation(res.data.id, DEFAULT_TITLE);
            const newConv = { ...res.data, title: DEFAULT_TITLE };
            setConversations((prev) => [newConv, ...prev]);
            setActiveConvId(newConv.id);
            setMessages([]);
        } catch (err) {
            console.error("Failed to create conversation:", err);
        }
    }, []);

    const handleSwitchConversation = useCallback(async (conv) => {
        if (renamingId) return;
        setActiveConvId(conv.id);
        await loadMessages(conv.id);
    }, [renamingId, loadMessages]);

    const handleDeleteConversation = useCallback(async (e, convId) => {
        e.stopPropagation();
        try {
            await apiDeleteConversation(convId);
            setConversations((prev) => prev.filter((c) => c.id !== convId));
            if (activeConvId === convId) {
                setActiveConvId(null);
                setMessages([]);
            }
            if (renamingId === convId) setRenamingId(null);
        } catch (err) {
            console.error("Failed to delete conversation:", err);
        }
    }, [activeConvId, renamingId]);

    /* ── rename ── */
    const startRename = useCallback((e, conv) => {
        e.stopPropagation();
        setRenamingId(conv.id);
        setRenameValue(conv.title);
    }, []);

    const commitRename = useCallback(async () => {
        const trimmed = renameValue.trim();
        if (trimmed && renamingId) {
            try {
                await renameConversation(renamingId, trimmed);
                setConversations((prev) =>
                    prev.map((c) => c.id === renamingId ? { ...c, title: trimmed } : c)
                );
            } catch (err) {
                console.error("Failed to rename conversation:", err);
            }
        }
        setRenamingId(null);
    }, [renameValue, renamingId]);

    const handleRenameKey = useCallback((e) => {
        if (e.key === "Enter")  { e.preventDefault(); commitRename(); }
        if (e.key === "Escape") { e.stopPropagation(); setRenamingId(null); }
    }, [commitRename]);

    /* ── send message ── */
    const sendQuestion = async (question) => {
        let convId = activeConvId;
        if (convId === null || convId === undefined) {
            try {
                const res = await createConversation(userUuidRef.current);
                const DEFAULT_TITLE = "New conversation";
                await renameConversation(res.data.id, DEFAULT_TITLE);
                const newConv = { ...res.data, title: DEFAULT_TITLE };
                setConversations((prev) => [newConv, ...prev]);
                setActiveConvId(newConv.id);
                convId = newConv.id;
            } catch (err) {
                console.error("Failed to auto-create conversation:", err);
                return;
            }
        }

        const userMsg = { type: "user", text: question, timestamp: Date.now() };
        setMessages((prev) => [...prev, userMsg]);
        setIsTyping(true);

        try {
            const response = await askQuestion(question, userUuidRef.current, convId);

            setMessages((prev) => [
                ...prev,
                {
                    type: "agent",
                    text: response.data.answer,
                    timestamp: Date.now(),
                    sources: Array.isArray(response.data.sources) ? response.data.sources : [],
                },
            ]);

            setConversations((prev) =>
                prev.map((c) => {
                    if (c.id !== convId) return c;
                    const isDefault = !c.title || c.title === "New conversation";
                    if (!isDefault) return c;
                    const autoTitle = question.slice(0, 30) + (question.length > 30 ? "…" : "");
                    renameConversation(convId, autoTitle).catch((err) => {
                        console.error("Auto rename failed:", err);
                    });
                    return { ...c, title: autoTitle };
                })
            );
        } catch (err) {
            console.error("askQuestion error:", err?.response?.data || err);
            setMessages((prev) => [
                ...prev,
                {
                    type: "agent",
                    text: "An error occurred while processing your question. Please try again.",
                    timestamp: Date.now(),
                    sources: [],
                },
            ]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleSubmit = async () => {
        const question = input.trim();
        if (!question) return;
        setInput("");
        await sendQuestion(question);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
    };

    /* ─── render ─────────────────────────────────────────────── */
    return createPortal(
        <div
            className={[
                "chat-panel",
                minimized   ? "minimized"    : "",
                sidebarOpen ? "sidebar-open" : "",
            ].filter(Boolean).join(" ")}
        >
            {/* ── Header ── */}
            <div className="chat-header">
                <div className="header-content">
                    <div className="assistant-info">
                        <div className="assistant-avatar">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="sparkles-icon">
                                <rect x="3" y="11" width="18" height="10" rx="2" />
                                <circle cx="12" cy="5" r="2" />
                                <line x1="12" y1="7"  x2="12" y2="11" />
                                <line x1="8"  y1="15" x2="8"  y2="15" strokeLinecap="round" strokeWidth="3" />
                                <line x1="12" y1="15" x2="12" y2="15" strokeLinecap="round" strokeWidth="3" />
                                <line x1="16" y1="15" x2="16" y2="15" strokeLinecap="round" strokeWidth="3" />
                            </svg>
                        </div>
                        <div className="assistant-text">
                            <h3>Ottoman AI Assistant</h3>
                            <p>Ask about the manuscript corpus</p>
                        </div>
                    </div>

                    <div className="header-actions">
                        <button
                            className="conversations-button"
                            onClick={() => setSidebarOpen((p) => !p)}
                            title={sidebarOpen ? "Hide conversations" : "Show conversations"}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                <line x1="8" y1="6"  x2="21" y2="6"  />
                                <line x1="8" y1="12" x2="21" y2="12" />
                                <line x1="8" y1="18" x2="21" y2="18" />
                                <line x1="3" y1="6"  x2="3.01" y2="6"  strokeLinecap="round" strokeWidth="3" />
                                <line x1="3" y1="12" x2="3.01" y2="12" strokeLinecap="round" strokeWidth="3" />
                                <line x1="3" y1="18" x2="3.01" y2="18" strokeLinecap="round" strokeWidth="3" />
                            </svg>
                        </button>

                        <button className="new-conversation-button" onClick={handleCreateConversation} title="New conversation">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                <line x1="12" y1="5"  x2="12" y2="19" />
                                <line x1="5"  y1="12" x2="19" y2="12" />
                            </svg>
                        </button>

                        <button className="reduce-button" onClick={() => setMinimized((p) => !p)} title={minimized ? "Restore" : "Minimize"}>
                            {minimized
                                ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><polyline points="18 15 12 9 6 15" /></svg>
                                : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><polyline points="6 9 12 15 18 9" /></svg>
                            }
                        </button>

                        <button className="close-button" onClick={onClose} title="Close">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                <line x1="18" y1="6"  x2="6"  y2="18" />
                                <line x1="6"  y1="6"  x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Body ── */}
            {!minimized && (
                <div className="chat-body">

                    {/* Sidebar */}
                    <div className="conversations-sidebar">
                        <div className="sidebar-header">
                            <span>Chats</span>
                            <button className="new-conv-sidebar-button" onClick={handleCreateConversation}>+ New</button>
                        </div>

                        {convsLoading ? (
                            <div className="sidebar-loading">Loading…</div>
                        ) : conversations.length === 0 ? (
                            <div className="sidebar-empty">No conversations yet</div>
                        ) : (
                            <ul className="conversations-list">
                                {conversations.map((conv) => (
                                    <li
                                        key={conv.id}
                                        className={`conversation-item ${conv.id === activeConvId ? "active" : ""}`}
                                        onClick={() => handleSwitchConversation(conv)}
                                    >
                                        {renamingId === conv.id ? (
                                            <input
                                                ref={renameInputRef}
                                                className="conv-rename-input"
                                                value={renameValue}
                                                onChange={(e) => setRenameValue(e.target.value)}
                                                onBlur={commitRename}
                                                onKeyDown={handleRenameKey}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        ) : (
                                            <>
                                                <span
                                                    className="conv-title"
                                                    onDoubleClick={(e) => startRename(e, conv)}
                                                    title="Double-click to rename"
                                                >
                                                    {conv.title}
                                                </span>

                                                <div className="conv-actions">
                                                    <button
                                                        className="conv-rename-button"
                                                        onClick={(e) => startRename(e, conv)}
                                                        title="Rename"
                                                    >
                                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11">
                                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                        </svg>
                                                    </button>

                                                    <button
                                                        className="conv-delete-button"
                                                        onClick={(e) => handleDeleteConversation(e, conv.id)}
                                                        title="Delete"
                                                    >
                                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11">
                                                            <line x1="18" y1="6"  x2="6"  y2="18" />
                                                            <line x1="6"  y1="6"  x2="18" y2="18" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Main chat column */}
                    <div className="chat-main">
                        <div className="messages-area">
                            {messages.length === 0 && (
                                <div className="welcome-message">
                                    <div className="welcome-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <rect x="3" y="11" width="18" height="10" rx="2" />
                                            <circle cx="12" cy="5" r="2" />
                                            <line x1="12" y1="7" x2="12" y2="11" />
                                        </svg>
                                    </div>
                                    <p>Ask a question about the Ottoman manuscript corpus.</p>
                                </div>
                            )}

                            {messages.map((message, index) => (
                                <MessageBubble
                                    key={message.id || index}
                                    type={message.type}
                                    text={message.text}
                                    timestamp={message.timestamp}
                                    sources={message.sources || []}
                                />
                            ))}

                            {isTyping && <TypingIndicator />}
                            <div ref={messagesEndRef} />
                        </div>

                        <div className="chat-input-area">
                            <div className="input-container">
                                <input
                                    className="message-input"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Ask about Ottoman manuscripts..."
                                    disabled={isTyping}
                                />
                                <button
                                    className="send-button"
                                    onClick={handleSubmit}
                                    disabled={!input.trim() || isTyping}
                                    title="Send"
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="22" y1="2" x2="11" y2="13" />
                                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
            )}
        </div>,
        document.body
    );
};

ChatPanel.propTypes = { onClose: PropTypes.func.isRequired };
export default ChatPanel;